// src/components/DashboardSection.js

import React from "react"; // Removed useEffect, useState
import {
  Container,
  Box,
  Typography, // Keep Typography if used in error/loading messages
  Paper,
  Grid,
  CircularProgress,
  Fade,
  // Removed Button, Avatar, Divider, Card, CardContent, IconButton, useMediaQuery
} from "@material-ui/core";
// Removed unused icons import {} from "@material-ui/icons";
import Section from "./Section";
import SectionHeader from "./SectionHeader";
import WelcomeCard from "./WelcomeCard";
import DashboardStatsBar from "./DashboardStatsBar";
import QuickLinksFooter from "./QuickLinksFooter";
import { useRouter } from "../util/router"; // Removed Link
import { useAuth } from "../util/auth";
import useDashboardSectionStyles from "./useDashboardSectionStyles";
import GenerationsList from "./GenerationsList";
// Removed ContextualCreditsDisplay (used in WelcomeCard)
import { Alert } from "@material-ui/lab";
import { useTheme } from "@material-ui/core/styles";
// Removed FormContext
import useFormData from "../hooks/useFormData";
import useUserGenerations from "../hooks/useUserGenerations";

// Remove AboutSection import if it's only used in QuickLinksFooter now
// import AboutSection from "./AboutSection";

// Removed AboutSection import if it's only used in QuickLinksFooter now

function DashboardSection(props) {
  const classes = useDashboardSectionStyles();
  const auth = useAuth();
  const router = useRouter();
  // Removed theme, isMobile as they are not used directly here

  // Use the custom hooks
  const {
    formData,
    loading: formLoading,
    error: formError,
    clearFormData,
  } = useFormData();
  const {
    generations,
    loading: loadingGenerations,
    error: generationsError,
    setGenerations, // Keep setGenerations if GenerationsList modifies it directly
  } = useUserGenerations();

  // Handle Continue Editing button click
  const handleContinueEditing = () => {
    router.push("/pro");
  };

  // Handle Start New Resume button click
  const handleStartNew = () => {
    clearFormData(); // Use the function from the hook
    if (!formError) {
      // Only navigate if clearing didn't cause an error
      router.push("/pro");
    }
  };

  // Calculate dashboard stats
  const dashboardStats = React.useMemo(() => {
    if (!generations.length) {
      // Return mock data if no generations
      return {
        totalApplications: 0,
        activeApplications: 0,
        interviewsCount: 0,
        offersCount: 0,
        mostRecent: null,
      };
    }

    const totalApplications = generations.length;
    const activeApplications = generations.filter(
      (gen) => gen.status !== "closed"
    ).length;
    const interviewsCount = generations.filter(
      (gen) => gen.progress === 1
    ).length;
    const offersCount = generations.filter((gen) => gen.progress === 2).length;

    // Get most recent application
    const sortedByDate = [...generations].sort((a, b) => {
      const dateA = a.timestamp ? new Date(a.timestamp) : new Date(0);
      const dateB = b.timestamp ? new Date(b.timestamp) : new Date(0);
      return dateB - dateA;
    });

    const mostRecent = sortedByDate.length > 0 ? sortedByDate[0] : null;

    return {
      totalApplications,
      activeApplications,
      interviewsCount,
      offersCount,
      mostRecent,
    };
  }, [generations]);

  // Removed formatDate as it's likely only used in GenerationsList

  const isLoading = formLoading || loadingGenerations;

  return (
    // No need for FormContext.Provider here unless other child components deep down need it
    // If GenerationsList or others need formData, pass it as a prop.
    <Section {...props}>
      <Container className={classes.container}>
        <SectionHeader
          title="Dashboard"
          subtitle=""
          size={4}
          textAlign="center"
          className={classes.sectionHeader}
        />

        {router.query.paid && auth.user?.planIsActive && (
          <Box className={classes.alertBox}>
            <Alert severity="success">
              You are now subscribed to the {auth.user.planId} plan
              <span
                role="img"
                aria-label="party"
                style={{ marginLeft: "10px" }}
              >
                🥳
              </span>
            </Alert>
          </Box>
        )}

        {/* Error Alerts */}
        {formError && (
          <Box mb={3}>
            <Alert severity="error">{formError}</Alert>
          </Box>
        )}
        {generationsError && (
          <Box mb={3}>
            <Alert severity="error">{generationsError}</Alert>
          </Box>
        )}

        {/* Loading Indicators */}
        {isLoading ? (
          <Box display="flex" justifyContent="center" my={5}>
            <CircularProgress aria-label="Loading dashboard data..." />
          </Box>
        ) : (
          <Fade in={!isLoading} timeout={500}>
            <div>
              {/* Use WelcomeCard Component */}
              <WelcomeCard
                formData={formData}
                onStartNew={handleStartNew}
                onContinueEditing={handleContinueEditing}
              />

              {/* Use DashboardStatsBar Component */}
              {/* Pass calculated stats and loading state */}
              <DashboardStatsBar stats={dashboardStats} loading={isLoading} />

              <Grid container spacing={3}>
                {/* Main Content Area - Applications List */}
                <Grid item xs={12}>
                  <Paper className={classes.contentPaper} elevation={2}>
                    <Box p={3} pb={1}>
                      <Typography variant="h6" gutterBottom>
                        Your Applications
                      </Typography>
                    </Box>
                    {/* Pass necessary props to GenerationsList */}
                    <GenerationsList
                      auth={auth}
                      generations={generations}
                      setGenerations={setGenerations} // Pass down if needed for deletion/updates within the list
                      loadingGenerations={loadingGenerations} // Pass loading state if needed by the list itself
                    />
                  </Paper>
                </Grid>
              </Grid>

              {/* Use QuickLinksFooter Component */}
              <QuickLinksFooter />
            </div>
          </Fade>
        )}
      </Container>
    </Section>
  );
}

export default React.memo(DashboardSection);
